import { Box, Grid2, Typography, useMediaQuery, useTheme } from "@mui/material";
import { useMeasure } from "react-use";
import styled from "@emotion/styled";
import { useMandatorContext } from "@rentablo/theming";
import ArrowLeft from "@icons/arrow-left.svg?react";
import { toCurrency } from "@helpers";
import { DonutChartDataItem } from "../highcharts";

const LegendWrapper = styled(Box)({
  position: "relative",
  display: "flex",
  alignItems: "center",
  flexDirection: "column",
});

const LegendChildrenWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== "height" && prop !== "isOpen",
})<{ height: number; isOpen: boolean }>(({ height, isOpen }) => ({
  height: isOpen ? `${height}px` : 0,
  overflow: "hidden",
  transition: "all 0.2s ease-in-out",
  width: "100%",
}));

const LegendChild = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: theme.spacing(2),
  paddingTop: theme.spacing(2),
  width: "100%",
  minWidth: 0,
}));

const LegendWrapperBackground = styled(Box, {
  shouldForwardProp: (prop) => prop !== "percent" && prop !== "color",
})<{ percent: number; color?: string }>(({ percent, color }) => ({
  width: `${percent}%`,
  height: "40px",
  position: "absolute",
  left: 0,
  top: 0,
  zIndex: 3,
  borderRadius: "4px",
  display: "flex",
  alignItems: "space-between",
  backgroundColor: color,
  overflow: "hidden",
}));

const LegendWrapperContent = styled(Grid2, {
  shouldForwardProp: (prop) => prop !== "isClickable",
})<{ isClickable: boolean }>(({ theme, isClickable }) => ({
  position: "relative",
  padding: theme.spacing(1, 2),
  width: "100%",
  height: "40px",
  boxSizing: "border-box",
  cursor: isClickable ? "pointer" : "default",
  spacing: "12px",
}));

const LegendIcon = styled(Box)(() => ({
  width: "20px",
  height: "20px",
  flexShrink: 0,

  "& svg": {
    width: "100%",
    height: "100%",
  },
}));

const ArrowRight = styled(ArrowLeft, {
  shouldForwardProp: (prop) => prop !== "isOpen" && prop !== "isVisible",
})<{ isOpen: boolean; isVisible: boolean }>(({ isOpen, isVisible }) => ({
  transform: isOpen ? "rotate(270deg)" : "rotate(180deg)",
  transition: isOpen ? "transform 0.2s ease-in-out" : "none",
  opacity: isVisible ? 1 : 0,
}));

interface Props {
  item: DonutChartDataItem;
  percent: number;
  isOpen: boolean;
  currencySignAfter: boolean;
  overlayColors: boolean;
  handleLegendClick: (item: DonutChartDataItem) => void;
}

export const LegendRow = ({
  item,
  percent,
  isOpen,
  currencySignAfter,
  overlayColors,
  handleLegendClick,
}: Props) => {
  const theme = useTheme();
  const { mode } = useMandatorContext();
  const isLessSm = useMediaQuery(theme.breakpoints.down("sm"));
  const [nameRef, { width: nameWidth }] = useMeasure<HTMLDivElement>();
  const [amountRef, { width: amountWidth }] = useMeasure<HTMLDivElement>();

  const childrenHeight =
    (item.children?.length || 0) * 32 + (item.children?.length ? 8 : 0);

  const hasChildren = Boolean(item.children?.length);

  return (
    <LegendWrapper key={item.name} onClick={() => handleLegendClick(item)}>
      <LegendWrapperBackground
        percent={percent}
        color={
          item.legendColor || item.color
            ? item.legendColor || item.color
            : item.value > 0
              ? theme.palette.background.success
              : theme.palette.background.neutral
        }
        sx={{
          cursor: hasChildren ? "pointer" : "default",
        }}
      >
        {overlayColors && (
          <>
            <Box
              display="flex"
              alignItems="center"
              gap={3}
              ml={theme.spacing(2)}
              color={item.textColor ? item.textColor : "inherit"}
              minWidth={nameWidth}
            >
              <LegendIcon>{item.icon}</LegendIcon>
              <Typography variant={isLessSm ? "body2" : "body1"} noWrap>
                {item.name}
              </Typography>
            </Box>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="flex-end"
              gap={theme.spacing(3)}
              color={
                mode === "light"
                  ? theme.palette.text.primary
                  : theme.palette.text.onColorDark
              }
              minWidth={`${amountWidth}px`}
              sx={{
                cursor: hasChildren ? "pointer" : "default",
              }}
            >
              <Typography variant="body1">
                {toCurrency(item.value, "EUR", currencySignAfter, {
                  notation: "compact",
                  compactDisplay: "short",
                  maximumFractionDigits: 2,
                })}
              </Typography>
              <Box flexShrink={0} display="flex" alignItems="center">
                <ArrowRight
                  width="20px"
                  height="20px"
                  isOpen={isOpen}
                  isVisible={hasChildren}
                />
              </Box>
            </Box>
          </>
        )}
      </LegendWrapperBackground>
      <LegendWrapperContent isClickable container>
        <Grid2
          ref={nameRef}
          display="flex"
          alignItems="center"
          gap={3}
          zIndex={overlayColors ? 0 : 4}
          color="inherit"
          size={{
            xs: 6,
            md: 7,
          }}
        >
          <LegendIcon>{item.icon}</LegendIcon>
          <Typography variant={isLessSm ? "body2" : "body1"} noWrap>
            {item.name}
          </Typography>
        </Grid2>
        <Grid2
          display="flex"
          alignItems="center"
          justifyContent="flex-end"
          gap={3}
          flexShrink={0}
          zIndex={overlayColors ? 0 : 4}
          ref={amountRef}
          size={{
            xs: 6,
            md: 5,
          }}
        >
          <Typography variant="body1">
            {toCurrency(item.value, "EUR", currencySignAfter, {
              notation: "compact",
              compactDisplay: "short",
              maximumFractionDigits: 2,
            })}
          </Typography>
          <Box flexShrink={0} display="flex" alignItems="center">
            <ArrowRight
              width="20px"
              height="20px"
              isOpen={isOpen}
              isVisible={hasChildren}
            />
          </Box>
        </Grid2>
      </LegendWrapperContent>
      <LegendChildrenWrapper isOpen={isOpen} height={childrenHeight}>
        {item.children?.map((child) => {
          const value = toCurrency(child.value, "EUR", currencySignAfter);
          if (value === toCurrency(0, "EUR", currencySignAfter)) return null;

          return (
            <LegendChild key={child.id}>
              <Typography variant="body3" pl={10} noWrap>
                {child.name}
              </Typography>
              <Typography variant="body4" pr={10} flexShrink={0}>
                {value}
              </Typography>
            </LegendChild>
          );
        })}
      </LegendChildrenWrapper>
    </LegendWrapper>
  );
};
